/**
 * FilterChip — pill-shaped filter toggle component.
 *
 * Standalone with no app-specific imports.
 * External dependencies: styled-components ≥5, db-npm-e4sc-rd-ui (Color, RddkTheme)
 *
 * To move into db-npm-e4sc-rd-ui:
 *   1. Copy to src/Components/FilterChip/FilterChip.tsx
 *   2. Change import path: `import { Color, RddkTheme } from '../../Foundation'`
 *   3. Add `export * from './FilterChip'` to src/Components/index.ts
 */
import { Color, RddkTheme } from 'db-npm-e4sc-rd-ui';
import React, { ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FilterChipProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Whether this chip represents the currently active/selected filter */
  selected?: boolean;
};

// ---------------------------------------------------------------------------
// Styled primitives
// ---------------------------------------------------------------------------

const StyledChip = styled.button<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px 4px;
  font-family: ${RddkTheme.fonts.sans};
  font-size: 14px;
  font-weight: ${(p) => (p.selected ? 600 : 400)};
  line-height: 1.5;
  border-radius: 100px;
  border: 1.5px solid ${(p) => (p.selected ? Color.rdRed : Color.rdGrey)};
  background: ${(p) => (p.selected ? Color.rdRed : Color.rdBrightWhite)};
  color: ${(p) => (p.selected ? '#fff' : Color.rdPureBlack)};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  user-select: none;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    border-color: ${Color.rdRed};
    background: ${(p) => (p.selected ? Color.rdRedHover : Color.rdLightRed)};
    color: ${(p) => (p.selected ? '#fff' : Color.rdRed)};
  }

  &:focus-visible {
    outline: 2px solid ${Color.rdRed};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/**
 * Dimmed article count to render inside a FilterChip.
 *
 * Usage: <FilterChip selected={...}>2025 <FilterChipCount>82</FilterChipCount></FilterChip>
 */
export const FilterChipCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  opacity: 0.7;
`;

/**
 * Flex-wrap container for a row of FilterChip components.
 * No margin — apply layout spacing in the consuming context.
 */
export const FilterChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Pill-shaped filter toggle. Stateless controlled component.
 *
 * @example
 * <FilterChip selected={activeYear === '2025'} onClick={() => setActiveYear('2025')}>
 *   2025 <FilterChipCount>82</FilterChipCount>
 * </FilterChip>
 */
export const FilterChip: React.FC<FilterChipProps> = ({ selected = false, children, ...rest }) => (
  <StyledChip selected={selected} type="button" {...rest}>
    {children}
  </StyledChip>
);
